import type { SizeLocales } from "@lib/constants/filtersConstants";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { shoeConventionForSite } from "@config/site/site-config";
import type { Dispatch, SetStateAction } from "react";
import { SelectDropdownItem } from "@ecco/ui/select-dropdown";

interface SizeSwitcherProps {
    sizeLocale: SizeLocales;
    setSizeLocale: Dispatch<SetStateAction<SizeLocales>>;
}

export const SizeSwitcher = ({ sizeLocale, setSizeLocale }: SizeSwitcherProps) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.size.labelEU,
            CommonCMS.size.labelUS,
            CommonCMS.size.labelUK,
            CommonCMS.size.labelCM,
            CommonCMS.size.ariaEU,
            CommonCMS.size.ariaUS,
            CommonCMS.size.ariaUK,
            CommonCMS.size.ariaCM,
        ]);
    })();
    const shoeSizeConvention = shoeConventionForSite;

    const availableShoeSizes = shoeSizeConvention.map((item) => {
        const label = microcopies[CommonCMS.size[`label${item}`]] || item;
        return (
            <SwitcherButton
                key={`avail${item}`}
                item={item}
                label={label}
                setSizeLocale={setSizeLocale}
                sizeLocale={sizeLocale as string}
            />
        );
    });
    return (
        <Box data-testid="size-switcher" display="flex" flexDirection="row">
            {availableShoeSizes}
        </Box>
    );
};

export const SizeSwitcherDropdown = ({ sizeLocale, setSizeLocale }: SizeSwitcherProps) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.size.labelEU,
            CommonCMS.size.labelUS,
            CommonCMS.size.labelUK,
            CommonCMS.size.labelCM,
        ]);
    })();
    const shoeSizeConvention = shoeConventionForSite;
    const availableShoeSizes = shoeSizeConvention.map((item) => {
        const label = microcopies[CommonCMS.size[`label${item}`]] || item;
        return (
            <Box key={item} display="flex" flexDirection="row" maxWidth="100%">
                <SwitcherButtonItem
                    item={item}
                    label={label}
                    setSizeLocale={setSizeLocale}
                    sizeLocale={sizeLocale}
                />
            </Box>
        );
    });
    return (
        <Box
            data-testid="size-selection-buttons"
            display="flex"
            flexDirection="row"
            maxWidth="100%"
        >
            {availableShoeSizes}
        </Box>
    );
};

const SwitcherButton = ({ sizeLocale, setSizeLocale, label, item }) => {
    const isCurrentLocale = sizeLocale === item;

    return (
        <Button
            size="sm"
            py={1}
            px={3}
            h="fit-content"
            fontSize="desktopH4"
            variant={isCurrentLocale ? "solid" : "standard"}
            onClick={() => {
                if (isCurrentLocale) return;
                setSizeLocale(item);
            }}
        >
            <span>{label} </span>
        </Button>
    );
};

const SwitcherButtonItem = ({ sizeLocale, setSizeLocale, label, item }) => {
    const isCurrentLocale = sizeLocale === item;

    return (
        <Button
            size="xs"
            as={SelectDropdownItem}
            fontSize="desktopBodyTextNormal"
            variant={isCurrentLocale ? "solid" : "standard"}
            onClick={() => {
                if (isCurrentLocale) return;
                setSizeLocale(item);
            }}
            _focusVisible={{
                bg: isCurrentLocale && "black",
                outline: "2px solid black",
                zIndex: 1,
            }}
        >
            <span>{label}</span>
        </Button>
    );
};
